import React, { useContext } from 'react';
import {
    Text,
    TextField,
    Dropdown,
    Stack,
    IconButton,
    DefaultButton,
    MessageBar,
    MessageBarType,
    TooltipHost,
} from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import { DataChannelOverride } from '../models/EnvironmentModels';
import { DataChannelOverrideCard } from './DataChannelOverrideCard';

interface DataChannelOverridesListProps {
    overrides: DataChannelOverride[];
    searchText: string;
    filterPipeline: number | null;
    filterRegion: number | null;
    filterWorkLane: number | null;
    filterEnabled: boolean | null;
    collapsedOverrides: Set<number>;
    overrideFilterTypes: Map<number, 'serviceTree' | 'buildPipeline'>;
    onSearchTextChange: (text: string) => void;
    onFilterPipelineChange: (value: number | null) => void;
    onFilterRegionChange: (value: number | null) => void;
    onFilterWorkLaneChange: (value: number | null) => void;
    onFilterEnabledChange: (value: boolean | null) => void;
    onClearFilters: () => void;
    onToggleCollapse: (index: number) => void;
    onFilterTypeChange: (
        index: number,
        type: 'serviceTree' | 'buildPipeline'
    ) => void;
    onFieldChange: (
        index: number,
        field: keyof DataChannelOverride,
        value: any
    ) => void;
    onCopyOverride: (override: DataChannelOverride) => void;
    onCopyToClipboard: (override: DataChannelOverride) => void;
    onRemoveOverride: (index: number) => void;
    onAddOverride: () => void;
    onAddFromUrl: () => void;
    onAddFromClipboard: () => void;
    onExportCsv: () => void;
    onImportCsv: () => void;
    isOverrideChanged: (index: number) => boolean;
    isOverrideActive: (override: DataChannelOverride) => boolean;
    filterOverrides: (
        overrides: DataChannelOverride[]
    ) => Array<{ override: DataChannelOverride; originalIndex: number }>;
}

export const DataChannelOverridesList: React.FC<DataChannelOverridesListProps> =
    ({
        overrides,
        searchText,
        filterPipeline,
        filterRegion,
        filterWorkLane,
        filterEnabled,
        collapsedOverrides,
        overrideFilterTypes,
        onSearchTextChange,
        onFilterPipelineChange,
        onFilterRegionChange,
        onFilterWorkLaneChange,
        onFilterEnabledChange,
        onClearFilters,
        onToggleCollapse,
        onFilterTypeChange,
        onFieldChange,
        onCopyOverride,
        onCopyToClipboard,
        onRemoveOverride,
        onAddOverride,
        onAddFromUrl,
        onAddFromClipboard,
        onExportCsv,
        onImportCsv,
        isOverrideChanged,
        isOverrideActive,
        filterOverrides,
    }) => {
        const themeContext = useContext(ThemeContext);
        const theme = getThemeFromString(themeContext.themeName);
        const placeholderStyles = {
            field: {
                selectors: {
                    '::placeholder': {
                        color: theme.palette.neutralQuaternary,
                        opacity: 0.7,
                        fontStyle: 'italic',
                    },
                },
            },
        };
        const filteredOverrides = filterOverrides(overrides);
        const hasActiveFilters =
            searchText.trim() !== '' ||
            filterPipeline !== null ||
            filterRegion !== null ||
            filterWorkLane !== null ||
            filterEnabled !== null;

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                {/* Search and Filter Section */}
                <div
                    style={{
                        padding: '16px',
                        backgroundColor: theme.palette.neutralLighterAlt,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralLight}`,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px',
                        }}
                    >
                        <Text
                            variant="medium"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Search & Filter
                        </Text>
                        {hasActiveFilters && (
                            <DefaultButton
                                text="Clear Filters"
                                iconProps={{ iconName: 'ClearFilter' }}
                                onClick={onClearFilters}
                                styles={{ root: { minWidth: 'auto' } }}
                            />
                        )}
                    </div>

                    <Stack tokens={{ childrenGap: 12 }}>
                        <TextField
                            placeholder="Search by name, service tree ID, account, project, branch, release ID, or tags..."
                            value={searchText}
                            onChange={(_, newValue) =>
                                onSearchTextChange(newValue || '')
                            }
                            iconProps={{ iconName: 'Search' }}
                            styles={placeholderStyles}
                        />

                        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                            <Dropdown
                                placeholder="Filter by Pipeline"
                                selectedKey={filterPipeline}
                                options={[
                                    { key: 0, text: 'Blob' },
                                    { key: 1, text: 'Core' },
                                    { key: 2, text: 'Message' },
                                ]}
                                onChange={(_, option) =>
                                    onFilterPipelineChange(
                                        option ? (option.key as number) : null
                                    )
                                }
                                styles={{ dropdown: { width: 150 } }}
                            />

                            <Dropdown
                                placeholder="Filter by Region"
                                selectedKey={filterRegion}
                                options={[
                                    { key: 0, text: 'Primary' },
                                    { key: 1, text: 'Secondary' },
                                ]}
                                onChange={(_, option) =>
                                    onFilterRegionChange(
                                        option ? (option.key as number) : null
                                    )
                                }
                                styles={{ dropdown: { width: 150 } }}
                            />

                            <Dropdown
                                placeholder="Filter by Work Lane"
                                selectedKey={filterWorkLane}
                                options={[
                                    { key: 0, text: 'Warm' },
                                    { key: 1, text: 'Dcmt' },
                                ]}
                                onChange={(_, option) =>
                                    onFilterWorkLaneChange(
                                        option ? (option.key as number) : null
                                    )
                                }
                                styles={{ dropdown: { width: 150 } }}
                            />

                            <Dropdown
                                placeholder="Filter by Enabled"
                                selectedKey={
                                    filterEnabled === null
                                        ? undefined
                                        : filterEnabled
                                        ? 'true'
                                        : 'false'
                                }
                                options={[
                                    { key: 'true', text: 'Enabled' },
                                    { key: 'false', text: 'Disabled' },
                                ]}
                                onChange={(_, option) => {
                                    if (option) {
                                        onFilterEnabledChange(
                                            option.key === 'true'
                                        );
                                    } else {
                                        onFilterEnabledChange(null);
                                    }
                                }}
                                styles={{ dropdown: { width: 150 } }}
                            />
                        </Stack>

                        {hasActiveFilters && (
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                Showing {filteredOverrides.length} of{' '}
                                {overrides.length} override
                                {overrides.length !== 1 ? 's' : ''}
                            </Text>
                        )}
                    </Stack>
                </div>

                {/* Header with count and Add buttons */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px',
                    }}
                >
                    {overrides.length > 0 ? (
                        <Text
                            variant="medium"
                            styles={{
                                root: { color: theme.palette.neutralSecondary },
                            }}
                        >
                            {overrides.length} override
                            {overrides.length !== 1 ? 's' : ''} total
                        </Text>
                    ) : (
                        <div />
                    )}
                    <div
                        style={{
                            display: 'flex',
                            gap: '4px',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                    >
                        <TooltipHost content="Add a new blank override">
                            <IconButton
                                iconProps={{ iconName: 'Add' }}
                                onClick={onAddOverride}
                                ariaLabel="Add a new blank override"
                                styles={{
                                    root: { color: theme.palette.themePrimary },
                                    rootHovered: {
                                        color: theme.palette.themeDarker,
                                    },
                                }}
                            />
                        </TooltipHost>
                        <TooltipHost content="Add override from Azure DevOps URL">
                            <IconButton
                                iconProps={{ iconName: 'Link' }}
                                onClick={onAddFromUrl}
                                ariaLabel="Add override from Azure DevOps URL"
                            />
                        </TooltipHost>
                        <TooltipHost content="Add override from clipboard data">
                            <IconButton
                                iconProps={{ iconName: 'ClipboardList' }}
                                onClick={onAddFromClipboard}
                                ariaLabel="Add override from clipboard data"
                            />
                        </TooltipHost>
                        <TooltipHost content="Import overrides from CSV file">
                            <IconButton
                                iconProps={{ iconName: 'Upload' }}
                                onClick={onImportCsv}
                                ariaLabel="Import overrides from CSV file"
                            />
                        </TooltipHost>
                        <TooltipHost content="Export all overrides to CSV file">
                            <IconButton
                                iconProps={{ iconName: 'Download' }}
                                onClick={onExportCsv}
                                disabled={overrides.length === 0}
                                ariaLabel="Export all overrides to CSV file"
                            />
                        </TooltipHost>
                    </div>
                </div>

                {overrides.length === 0 && (
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                fontStyle: 'italic',
                                color: theme.palette.neutralSecondary,
                            },
                        }}
                    >
                        No data channel overrides configured
                    </Text>
                )}

                {filteredOverrides.length === 0 && overrides.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        No overrides match your search criteria. Try adjusting
                        your filters.
                    </MessageBar>
                )}

                {filteredOverrides.map(({ override, originalIndex }) => {
                    const isChanged = isOverrideChanged(originalIndex);
                    const filterType =
                        overrideFilterTypes.get(originalIndex) || 'serviceTree';
                    const isCollapsed = collapsedOverrides.has(originalIndex);
                    const isActive = isOverrideActive(override);

                    return (
                        <DataChannelOverrideCard
                            key={originalIndex}
                            override={override}
                            index={originalIndex}
                            isChanged={isChanged}
                            isCollapsed={isCollapsed}
                            isActive={isActive}
                            filterType={filterType}
                            onToggleCollapse={() =>
                                onToggleCollapse(originalIndex)
                            }
                            onFilterTypeChange={(newType) =>
                                onFilterTypeChange(originalIndex, newType)
                            }
                            onFieldChange={(field, value) =>
                                onFieldChange(originalIndex, field, value)
                            }
                            onCopyOverride={() => onCopyOverride(override)}
                            onCopyToClipboard={() =>
                                onCopyToClipboard(override)
                            }
                            onRemove={() => onRemoveOverride(originalIndex)}
                        />
                    );
                })}
            </div>
        );
    };
